import { useEffect, useState } from "react";
import { api, type Session, type CrmContact } from "./api";
import { Badge, Empty, PageTitle, errorMessage } from "./components";

/** Read-only new-domain CRM surface. Never calls the Circle-era CRM service. */
export function CrmScreen({
  session,
  onAuth,
}: {
  session: Session;
  onAuth: () => void;
}) {
  const [contacts, setContacts] = useState<CrmContact[]>([]),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    setContacts([]);
    setError("");
    if (session.role === "crm") {
      setLoading(true);
      api
        .crmContacts()
        .then((r) => {
          if (active) setContacts(r.contacts);
        })
        .catch((e) => {
          if (active) setError(errorMessage(e));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }
    return () => {
      active = false;
    };
  }, [session.person?.id, session.role]);
  return (
    <>
      <PageTitle eyebrow="CRM · Nouveau réseau" title="Suivi relationnel privé">
        Identités communes, accès distincts. Aucune donnée de l’ancien CRM n’est
        importée.
      </PageTitle>
      {!session.person ? (
        <button className="button primary" onClick={onAuth}>
          Se connecter avec un compte CRM
        </button>
      ) : session.role !== "crm" ? (
        <Empty icon="shield" title="Un accès CRM est nécessaire.">
          Le compte membre et le rôle d’intendance réseau ne donnent pas accès
          aux notes privées.
        </Empty>
      ) : (
        <>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          {loading ? (
            <p role="status">Chargement des contacts…</p>
          ) : (
            <div className="stack">
              {contacts.map((contact) => (
                <article className="panel" key={contact.id}>
                  <div className="section-heading">
                    <h2>{contact.display_name}</h2>
                    <Badge>Relation de service</Badge>
                  </div>
                  <dl className="reference-list">
                    <div>
                      <dt>Identité commune</dt>
                      <dd>{contact.person_id}</dd>
                    </div>
                    <div>
                      <dt>Référence CRM</dt>
                      <dd>{contact.id}</dd>
                    </div>
                  </dl>
                  <p>
                    Prospection commerciale :{" "}
                    {contact.marketing_consent
                      ? "consentement enregistré"
                      : "aucun consentement"}
                  </p>
                  <details>
                    <summary>
                      Notes CRM privées ({contact.notes.length})
                    </summary>
                    {contact.notes.length ? (
                      contact.notes.map((note) => (
                        <p key={note.id}>{note.body}</p>
                      ))
                    ) : (
                      <p>Aucune note.</p>
                    )}
                  </details>
                  {contact.tree_references.map((ref) => (
                    <div className="note block" key={ref.id}>
                      <strong>
                        {ref.synthetic ? "Référence synthétique · " : ""}
                        {ref.catalog_id} / {ref.node_id}
                      </strong>
                      <p>{ref.justification}</p>
                      <p className="fineprint">
                        Provenance : {ref.provenance}. Ce lien ne prouve ni
                        compétence ni disponibilité.
                      </p>
                    </div>
                  ))}
                </article>
              ))}
            </div>
          )}
          <p className="fineprint">
            Cette vue lit le nouveau domaine CRM. Le client d’intégration du
            dépôt avalon-crm reste à activer dans son interface historique.
          </p>
        </>
      )}
    </>
  );
}
